import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Grid, Card, Button,
    Fade, Grow
} from '@mui/material';
import {
    Award, Users, Globe, TrendingUp, Heart, Shield,
    CheckCircle, Star
} from 'lucide-react';
import Footer from './Footer';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
    <Grow in timeout={1000 + delay}>
        <Card
            sx={{
                height: '100%',
                textAlign: 'center',
                p: 4,
                borderRadius: 3,
                transition: 'all 0.3s',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                },
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    color: 'white',
                }}
            >
                <Icon size={40} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                {title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {description}
            </Typography>
        </Card>
    </Grow>
);

const ValueCard = ({ icon: Icon, title, description }) => (
    <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
        <Box
            sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0,
            }}
        >
            <Icon size={28} />
        </Box>
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {description}
            </Typography>
        </Box>
    </Box>
);

const AboutPage = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: Award,
            title: '60+ Years of Excellence',
            description: 'Since 1966, we have been delivering quality food products with unwavering commitment to excellence.',
            delay: 0
        },
        {
            icon: Users,
            title: '3,000+ Happy Customers',
            description: 'Trusted by thousands of customers across the country for our quality and reliability.',
            delay: 200
        },
        {
            icon: Globe,
            title: 'Presence Across Rajasthan',
            description: 'Serving customers across Rajasthan with efficient logistics and timely delivery.',
            delay: 400
        },
    ];

    const values = [
        {
            icon: CheckCircle,
            title: 'Quality First',
            description: 'We never compromise on quality. Every product goes through rigorous quality checks to ensure only the best reaches you.'
        },
        {
            icon: Heart,
            title: 'Customer Satisfaction',
            description: 'Our customers are at the heart of everything we do. Their satisfaction drives us to continuously improve and innovate.'
        },
        {
            icon: Shield,
            title: 'Trust & Integrity',
            description: 'Built on decades of trust, we maintain the highest standards of business ethics and transparency in all our dealings.'
        },
        {
            icon: TrendingUp,
            title: 'Continuous Innovation',
            description: 'While respecting tradition, we embrace modern technology and processes to serve you better every day.'
        }
    ];

    return (
        <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                py: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}>
                <Container maxWidth="lg">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        >
                            <img
                                src="/Brand_logo.svg"
                                alt="Brand Logo"
                                style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    height: '64px',
                                    padding: '4px'
                                }}
                            />
                            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
                                Ramdayal Rameshwar Lal
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    }
                                }}
                                onClick={() => navigate('/')}
                            >
                                Home
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: 'white',
                                    color: '#667eea',
                                    '&:hover': {
                                        bgcolor: '#f0f0f0',
                                    }
                                }}
                                onClick={() => navigate('/login')}
                            >
                                Login
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 12,
                    textAlign: 'center',
                }}
            >
                <Container maxWidth="lg">
                    <Fade in timeout={1000}>
                        <Box>
                            <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
                                About Ramdayal Rameshwar Lal
                            </Typography>
                            <Typography variant="h5" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.95, lineHeight: 1.8 }}>
                                A legacy of trust, quality, and excellence spanning over six decades
                            </Typography>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* Our Story */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Fade in timeout={1200}>
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: '#667eea',
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        letterSpacing: 2
                                    }}
                                >
                                    OUR STORY
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3, mt: 1 }}>
                                    Building Trust Since 1966
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}>
                                    Ramdayal Rameshwar Lal was founded in 1966 by our Grandfather <strong>Rameshwar Manglunia</strong> with a simple yet powerful vision:
                                    to provide the finest quality food products to every household in India.
                                    What started as a small family business has grown into one of the most trusted
                                    names in Sujangarh.
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}>
                                    Over the decades, we have remained committed to our core values of quality,
                                    integrity, and customer satisfaction. Our products are sourced from the best
                                    farms and processed with the latest technology to ensure maximum freshness
                                    and nutritional value.
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                                    Today, we serve thousands of customers across Rajasthan, but our approach remains
                                    the same - treating every customer like family and every product as a promise
                                    of quality.
                                </Typography>
                            </Box>
                        </Fade>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Fade in timeout={1400}>
                            <Box
                                sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                }}
                            >
                                <img
                                    src="/Brand_logo.svg"
                                    alt="Our Legacy"
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        backgroundColor: 'white',
                                        padding: '40px',
                                    }}
                                />
                            </Box>
                        </Fade>
                    </Grid>
                </Grid>
            </Container>

            {/* Features Section */}
            <Box sx={{ bgcolor: 'white', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        align="center"
                        sx={{ fontWeight: 'bold', mb: 2 }}
                    >
                        Why Choose Us
                    </Typography>
                    <Typography
                        variant="h6"
                        align="center"
                        color="text.secondary"
                        sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}
                    >
                        We are more than just a food company. We are your partners in health and nutrition.
                    </Typography>
                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <FeatureCard {...feature} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Our Values */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography
                    variant="h3"
                    align="center"
                    sx={{ fontWeight: 'bold', mb: 2 }}
                >
                    Our Core Values
                </Typography>
                <Typography
                    variant="h6"
                    align="center"
                    color="text.secondary"
                    sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}
                >
                    The principles that guide everything we do
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Fade in timeout={1000}>
                            <Box>
                                {values.slice(0, 2).map((value, index) => (
                                    <ValueCard key={index} {...value} />
                                ))}
                            </Box>
                        </Fade>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Fade in timeout={1200}>
                            <Box>
                                {values.slice(2, 4).map((value, index) => (
                                    <ValueCard key={index} {...value} />
                                ))}
                            </Box>
                        </Fade>
                    </Grid>
                </Grid>
            </Container>

            {/* Mission & Vision */}
            <Box sx={{ bgcolor: 'white', py: 10 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6}>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    height: '100%',
                                    p: 5,
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                }}
                            >
                                <Star size={48} style={{ marginBottom: '16px' }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
                                    Our Mission
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, opacity: 0.95 }}>
                                    To deliver the highest quality food products that enrich lives and nourish
                                    communities, while maintaining sustainable and ethical business practices
                                    that benefit all stakeholders.
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    height: '100%',
                                    p: 5,
                                    borderRadius: 3,
                                    border: '2px solid',
                                    borderColor: '#667eea',
                                }}
                            >
                                <TrendingUp size={48} color="#667eea" style={{ marginBottom: '16px' }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, color: '#667eea' }}>
                                    Our Vision
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                                    To be the most trusted and preferred food products company in India,
                                    recognized for our unwavering commitment to quality, innovation, and
                                    customer satisfaction, while setting industry standards for excellence.
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 10,
                    textAlign: 'center',
                }}
            >
                <Container maxWidth="md">
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                        Experience Quality Like Never Before
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 5, opacity: 0.95 }}>
                        Join thousands of satisfied customers who trust us for their daily needs
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{
                                bgcolor: 'white',
                                color: '#667eea',
                                px: 5,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    bgcolor: '#f0f0f0',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                },
                                transition: 'all 0.3s',
                            }}
                            onClick={() => navigate('/products')}
                        >
                            View Our Products
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                px: 5,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s',
                            }}
                            onClick={() => navigate('/')}
                        >
                            Contact Us
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
};

export default AboutPage;
